/**
 * Minimal IndexedDB shim for offline inspection resilience (Phase 1). I/O only —
 * no app logic lives here, so the testable offline logic (markers, reconcile,
 * sync) stays in pure modules that run in the node vitest harness.
 *
 * Fail-soft: every call swallows IDB errors and resolves to a safe default, so a
 * device with IndexedDB disabled (Safari private browsing) degrades to today's
 * online-only behavior rather than throwing.
 *
 * iPad/Safari note: IndexedDB is supported (incl. Home-Screen PWA mode). Safari's
 * ITP may evict script-writable storage after ~7 days of NO site use; daily use
 * resets that, and we sync to the server as soon as there's signal, so IDB is a
 * short-lived buffer. requestPersistentStorage() asks Safari to keep it.
 */

const DB_NAME = 'irrigation-offline';
const DB_VERSION = 1;
export const STORE_DRAFTS = 'drafts';
export const STORE_PHOTO_BLOBS = 'photo_blobs';

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
          db.createObjectStore(STORE_DRAFTS, { keyPath: 'localId' });
        }
        if (!db.objectStoreNames.contains(STORE_PHOTO_BLOBS)) {
          db.createObjectStore(STORE_PHOTO_BLOBS, { keyPath: 'id' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest,
  fallback: T,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve) => {
        if (!db) {
          resolve(fallback);
          return;
        }
        try {
          const transaction = db.transaction(store, mode);
          const request = run(transaction.objectStore(store));
          request.onsuccess = () => resolve((request.result as T) ?? fallback);
          request.onerror = () => resolve(fallback);
          transaction.onerror = () => resolve(fallback);
          transaction.onabort = () => resolve(fallback);
        } catch {
          resolve(fallback);
        }
      }),
  );
}

export function idbGet<T>(store: string, key: IDBValidKey): Promise<T | null> {
  return tx<T | null>(store, 'readonly', (s) => s.get(key), null);
}

export function idbGetAll<T>(store: string): Promise<T[]> {
  return tx<T[]>(store, 'readonly', (s) => s.getAll(), []);
}

export function idbPut(store: string, value: unknown): Promise<boolean> {
  return tx<IDBValidKey | null>(store, 'readwrite', (s) => s.put(value as object), null).then(
    (k) => k !== null,
  );
}

export function idbDelete(store: string, key: IDBValidKey): Promise<void> {
  return tx<undefined>(store, 'readwrite', (s) => s.delete(key), undefined).then(() => undefined);
}

/** Ask the browser to keep our storage (reduces Safari ITP eviction). Best-effort. */
export async function requestPersistentStorage(): Promise<void> {
  try {
    if (navigator.storage?.persist && !(await navigator.storage.persisted())) {
      await navigator.storage.persist();
    }
  } catch {
    /* best-effort */
  }
}
